export type TranslationSourceProvider = "chrome" | "mymemory";

export interface TranslationResult {
  text: string;
  source: TranslationSourceProvider;
}

const ARABIC_REGEX = /[؀-ۿ]/;

function detectPair(text: string): { source: string; target: string } {
  const hasArabic = ARABIC_REGEX.test(text);
  return hasArabic ? { source: "ar", target: "en" } : { source: "en", target: "ar" };
}

class TranslationService {
  private async tryChrome(text: string): Promise<string | null> {
    try {
      const globalAny = self as unknown as { Translator?: any };
      if (!globalAny.Translator || typeof globalAny.Translator.create !== "function") {
        return null;
      }
      const { source, target } = detectPair(text);
      const translator = await globalAny.Translator.create({
        sourceLanguage: source,
        targetLanguage: target,
      });
      const result = await translator.translate(text);
      translator.destroy?.();
      return typeof result === "string" ? result : null;
    } catch {
      return null;
    }
  }

  private async tryMyMemory(text: string): Promise<string | null> {
    try {
      const { source, target } = detectPair(text);
      const params = new URLSearchParams({
        q: text,
        langpair: `${source}|${target}`,
      });
      const response = await fetch(
        `https://api.mymemory.translated.net/get?${params.toString()}`
      );
      if (!response.ok) return null;
      const data = await response.json();
      const translated = data?.responseData?.translatedText;
      return typeof translated === "string" && translated.length > 0 ? translated : null;
    } catch {
      return null;
    }
  }

  async translate(text: string): Promise<TranslationResult> {
    const chromeResult = await this.tryChrome(text);
    if (chromeResult) return { text: chromeResult, source: "chrome" };
    const myMemoryResult = await this.tryMyMemory(text);
    if (myMemoryResult) return { text: myMemoryResult, source: "mymemory" };
    throw new Error("translation_unavailable");
  }
}

export default new TranslationService();
